import { Component, OnInit, inject, computed, signal, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { DroneService } from '../../core/services/drone.service';
import { Drone, DroneOwnership, DroneStatus } from '../../core/models/drone.model';
import { ConfirmModalComponent } from '../../shared/components/confirm-modal/confirm-modal.component';
import { DateFormatPipe } from '../../shared/pipes/date-format.pipe';
import { MaxLengthDirective } from 'src/app/shared/directives/max-length.directive';

@Component({
  selector: 'app-drones',
  standalone: true,
  imports: [CommonModule, FormsModule, ConfirmModalComponent, DateFormatPipe, MaxLengthDirective],
  templateUrl: './drones.component.html',
  styleUrls: ['./drones.component.scss']
})
export class DronesComponent implements OnInit {
  @ViewChild('confirmModal') confirmModal!: ConfirmModalComponent;

  private droneService = inject(DroneService);
  private router = inject(Router);

  ngOnInit(): void {
    // Load drones from API
    this.droneService.loadOrganizationDrones();
  }

  // Filters
  searchQuery = signal('');
  ownershipFilter = signal<'all' | DroneOwnership>('all');
  statusFilter = signal<'all' | DroneStatus>('all');

  // Drone to delete
  droneToDelete: Drone | null = null;

  // Computed signals for filtered data
  readonly filteredDrones = computed(() => {
    const allDrones = this.droneService.drones();
    const query = this.searchQuery().toLowerCase();
    const ownership = this.ownershipFilter();
    const status = this.statusFilter();

    let results = allDrones;

    // Filter by ownership
    if (ownership !== 'all') {
      results = results.filter(d => d.ownership === ownership);
    }

    // Filter by status
    if (status !== 'all') {
      results = results.filter(d => d.status === status);
    }

    // Filter by search query
    if (query) {
      results = results.filter(d =>
        d.name.toLowerCase().includes(query) ||
        d.serialNumber.toLowerCase().includes(query) ||
        (d.modelName?.toLowerCase().includes(query) ?? false) ||
        (d.manufacturerName?.toLowerCase().includes(query) ?? false)
      );
    }

    return results;
  });

  // Computed stats
  readonly totalDrones = computed(() => this.droneService.totalDrones());
  readonly activeDrones = computed(() => this.droneService.activeDrones());
  readonly totalFlightHours = computed(() => this.droneService.totalFlightHours());
  readonly totalAcresSprayed = computed(() => this.droneService.totalAcresSprayed());

  // Navigation
  addDrone(): void {
    this.router.navigate(['/drones/add']);
  }

  viewDrone(drone: Drone): void {
    this.router.navigate(['/drones', drone.id, 'view']);
  }

  editDrone(drone: Drone): void {
    if (this.canEdit(drone)) {
      this.router.navigate(['/drones', drone.id, 'edit']);
    }
  }

  // Permission checks
  canEdit(drone: Drone): boolean {
    return this.droneService.canEdit(drone);
  }

  canDelete(drone: Drone): boolean {
    return this.droneService.canDelete(drone);
  }

  // Delete flow
  deleteDrone(drone: Drone): void {
    if (!this.canDelete(drone)) {
      return;
    }

    this.droneToDelete = drone;
    this.confirmModal.open({
      title: 'Delete Drone',
      message: `Are you sure you want to delete "${drone.name}"? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      confirmButtonClass: 'btn-danger',
      icon: 'bi-trash'
    });
  }

  async onDeleteConfirmed(): Promise<void> {
    if (this.droneToDelete) {
      try {
        await this.droneService.deleteDrone(this.droneToDelete.id);
      } catch (error: any) {
        console.error('[DronesComponent] Error deleting drone:', error);
        alert(error.message || 'Failed to delete drone');
      } finally {
        this.droneToDelete = null;
      }
    }
  }

  onDeleteCancelled(): void {
    this.droneToDelete = null;
  }

  // Filter helpers
  clearFilters(): void {
    this.searchQuery.set('');
    this.ownershipFilter.set('all');
    this.statusFilter.set('all');
  }

  hasActiveFilters(): boolean {
    return this.searchQuery() !== '' ||
           this.ownershipFilter() !== 'all' ||
           this.statusFilter() !== 'all';
  }

  // Get computed stats for a specific drone (from spray records)
  getDroneStats(droneId: string): { totalFlights: number; totalAcresSprayed: number; totalFlightHours: number; lastFlightDate: string | undefined } {
    return this.droneService.getDroneFlightStats(droneId);
  }

  // Formatting helpers
  formatStatus(status: DroneStatus): string {
    const labels: Record<DroneStatus, string> = {
      'active': 'Active',
      'maintenance': 'Maintenance',
      'inactive': 'Inactive',
      'retired': 'Retired'
    };
    return labels[status] || status;
  }

  getStatusClass(status: DroneStatus): string {
    const classes: Record<DroneStatus, string> = {
      'active': 'bg-success',
      'maintenance': 'bg-warning text-dark',
      'inactive': 'bg-secondary',
      'retired': 'bg-dark'
    };
    return classes[status] || 'bg-secondary';
  }

  formatOwnership(ownership: DroneOwnership): string {
    return ownership === 'adn' ? 'ADN' : 'Owned';
  }

  getOwnershipClass(ownership: DroneOwnership): string {
    return ownership === 'adn'
      ? 'bg-primary-subtle text-primary'
      : 'bg-success-subtle text-success';
  }
}
